import { Box, Typography } from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import { formatMoney } from '../cashbacks/formatMoney';
import { RecentActivity } from './RecentActivity';
import { StatCard } from './StatCard';
import { useDashboardStats } from './useDashboardStats';

/**
 * The landing dashboard — a region-driven, read-only stats surface (Phase 1
 * capstone). Every number and the activity feed are scoped to the ACTIVE region
 * and flip with the switcher; a single-region user sees only their market.
 * Visible to all authenticated roles (it is the landing screen).
 *
 * Stat cards now; the trend chart is DEFERRED until real data exists (no
 * charting library here). The layout is built to RECEIVE the chart additively —
 * see the slot comment below and DECISIONS.md D-dashboard.
 */
const Dashboard = () => {
  const { activeRegion } = useRegion();
  const { stats } = useDashboardStats();

  const money = (n: number | undefined) =>
    stats && n !== undefined ? formatMoney(n, stats.currency) : '—';
  const num = (n: number | undefined) => (n !== undefined ? String(n) : '—');
  const breaches = stats?.slaBreaches ?? 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Showing {activeRegion}
          {stats ? ` · ${stats.currency.toUpperCase()}` : ''}
        </Typography>
      </Box>

      {/* Stat card family — one designed row, responsive 6 → 3 → 2 → 1. */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(6, 1fr)',
          },
        }}
      >
        <StatCard
          label="Members"
          value={num(stats?.members)}
          testId="stat-members"
        />
        <StatCard
          label="Active stores"
          value={num(stats?.activeStores)}
          testId="stat-activeStores"
        />
        <StatCard
          label="Pending cashback"
          value={money(stats?.pendingCashback)}
          secondary="pending + waiting"
          testId="stat-pendingCashback"
        />
        <StatCard
          label="Confirmed cashback"
          value={money(stats?.confirmedCashback)}
          secondary="confirmed"
          testId="stat-confirmedCashback"
        />
        <StatCard
          label="Cashouts awaiting action"
          value={num(stats?.cashoutsAwaitingAction)}
          secondary={
            stats
              ? `${stats.cashoutsRequested} requested · ${stats.cashoutsProcessing} processing`
              : undefined
          }
          testId="stat-cashoutsAwaitingAction"
        />
        <StatCard
          label="SLA breaches"
          value={num(stats?.slaBreaches)}
          secondary={breaches > 0 ? 'Needs attention' : 'All clear'}
          tone={breaches > 0 ? 'alert' : 'default'}
          testId="stat-slaBreaches"
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        {/* Chart slot — a region trend chart (e.g. cashback over time) will mount
            HERE once real data exists. Deferred: no charting library and no
            placeholder UI (see D-dashboard). The layout receives it additively —
            it slots above Recent activity without restructuring. */}
        <RecentActivity />
      </Box>
    </Box>
  );
};

export default Dashboard;
